import Dexie, { type Table } from 'dexie';
import type { D2DArea, D2DStreet, D2DHouse, D2DUnit, D2DNote, D2DHouseNote } from '@/types/d2d';

export interface CachedArea extends D2DArea {
  cached_at: string;
}

export interface CachedStreet extends D2DStreet {
  cached_at: string;
}

export interface CachedHouse extends D2DHouse {
  cached_at: string;
}

export interface CachedUnit extends D2DUnit {
  cached_at: string;
}

export interface CachedNote extends D2DNote {
  cached_at: string;
}

export interface CachedHouseNote extends D2DHouseNote {
  cached_at: string;
}

export interface PendingChange {
  id: string;
  type: 'unit_update' | 'note_add' | 'note_delete' | 'unit_create';
  entity_id: string;
  payload: any;
  created_at: string;
  synced: boolean;
}

class OfflineDatabase extends Dexie {
  areas!: Table<CachedArea>;
  streets!: Table<CachedStreet>;
  houses!: Table<CachedHouse>;
  units!: Table<CachedUnit>;
  notes!: Table<CachedNote>;
  houseNotes!: Table<CachedHouseNote>;
  pendingChanges!: Table<PendingChange>;

  constructor() {
    super('d2d_offline_db');
    
    this.version(1).stores({
      areas: 'id, organization_id, created_by, cached_at',
      streets: 'id, area_id, cached_at',
      houses: 'id, street_id, cached_at',
      units: 'id, house_id, cached_at',
      notes: 'id, unit_id, cached_at',
      houseNotes: 'id, house_id, cached_at',
      pendingChanges: 'id, type, entity_id, synced, created_at'
    });
  }
}

export const offlineDb = new OfflineDatabase();

// Cache entire area with all nested data
export const cacheAreaData = async (
  area: D2DArea,
  streets: D2DStreet[],
  houses: D2DHouse[],
  units: D2DUnit[],
  notes: D2DNote[],
  houseNotes: D2DHouseNote[]
) => {
  const cachedAt = new Date().toISOString();
  
  await offlineDb.transaction('rw', 
    [offlineDb.areas, offlineDb.streets, offlineDb.houses, offlineDb.units, offlineDb.notes, offlineDb.houseNotes], 
    async () => {
      // Clear old data for this area
      await clearAreaCache(area.id);
      
      // Cache area
      await offlineDb.areas.put({ ...area, cached_at: cachedAt });
      
      // Cache streets
      if (streets.length > 0) {
        await offlineDb.streets.bulkPut(
          streets.map(s => ({ ...s, cached_at: cachedAt }))
        );
      }
      
      // Cache houses
      if (houses.length > 0) {
        await offlineDb.houses.bulkPut(
          houses.map(h => ({ ...h, cached_at: cachedAt }))
        );
      }
      
      // Cache units
      if (units.length > 0) {
        await offlineDb.units.bulkPut(
          units.map(u => ({ ...u, cached_at: cachedAt }))
        );
      }
      
      // Cache notes
      if (notes.length > 0) {
        await offlineDb.notes.bulkPut(
          notes.map(n => ({ ...n, cached_at: cachedAt }))
        );
      }
      
      // Cache house notes
      if (houseNotes.length > 0) {
        await offlineDb.houseNotes.bulkPut(
          houseNotes.map(n => ({ ...n, cached_at: cachedAt }))
        );
      }
    }
  );
  
  console.log(`✅ Cached area ${area.name}: ${streets.length} streets, ${houses.length} houses, ${units.length} units`);
};

// Clear cache for a specific area
export const clearAreaCache = async (areaId: string) => {
  const streets = await offlineDb.streets.where('area_id').equals(areaId).toArray();
  const streetIds = streets.map(s => s.id);
  
  if (streetIds.length > 0) {
    const houses = await offlineDb.houses.where('street_id').anyOf(streetIds).toArray();
    const houseIds = houses.map(h => h.id);
    
    if (houseIds.length > 0) {
      const units = await offlineDb.units.where('house_id').anyOf(houseIds).toArray();
      const unitIds = units.map(u => u.id);
      
      // Delete notes
      if (unitIds.length > 0) {
        await offlineDb.notes.where('unit_id').anyOf(unitIds).delete();
      }
      
      // Delete house notes
      await offlineDb.houseNotes.where('house_id').anyOf(houseIds).delete();
      
      // Delete units
      await offlineDb.units.where('house_id').anyOf(houseIds).delete();
    }
    
    // Delete houses
    await offlineDb.houses.where('street_id').anyOf(streetIds).delete();
  }
  
  // Delete streets
  await offlineDb.streets.where('area_id').equals(areaId).delete();
  
  // Delete area
  await offlineDb.areas.delete(areaId);
};

// Get cached area data
export const getCachedArea = async (areaId: string) => {
  const area = await offlineDb.areas.get(areaId);
  if (!area) return null;
  
  const streets = await offlineDb.streets.where('area_id').equals(areaId).toArray();
  const streetIds = streets.map(s => s.id);
  
  let houses: CachedHouse[] = [];
  let units: CachedUnit[] = [];
  let notes: CachedNote[] = [];
  let houseNotes: CachedHouseNote[] = [];
  
  if (streetIds.length > 0) {
    houses = await offlineDb.houses.where('street_id').anyOf(streetIds).toArray();
    const houseIds = houses.map(h => h.id);
    
    if (houseIds.length > 0) {
      units = await offlineDb.units.where('house_id').anyOf(houseIds).toArray();
      const unitIds = units.map(u => u.id);
      
      if (unitIds.length > 0) {
        notes = await offlineDb.notes.where('unit_id').anyOf(unitIds).toArray();
      }
      
      houseNotes = await offlineDb.houseNotes.where('house_id').anyOf(houseIds).toArray();
    }
  }
  
  return { area, streets, houses, units, notes, houseNotes };
};

// Check if area is cached
export const isAreaCached = async (areaId: string): Promise<boolean> => {
  const area = await offlineDb.areas.get(areaId);
  return !!area;
};

// Get all cached areas
export const getAllCachedAreas = async (): Promise<CachedArea[]> => {
  return await offlineDb.areas.toArray();
};

// Add pending change for offline sync
export const addPendingChange = async (change: Omit<PendingChange, 'id' | 'created_at' | 'synced'>) => {
  await offlineDb.pendingChanges.add({
    ...change,
    id: crypto.randomUUID(),
    created_at: new Date().toISOString(),
    synced: false
  });
};

// Get all pending changes
export const getPendingChanges = async (): Promise<PendingChange[]> => {
  return await offlineDb.pendingChanges.where('synced').equals(0).toArray();
};

// Mark change as synced
export const markChangeSynced = async (changeId: string) => {
  await offlineDb.pendingChanges.update(changeId, { synced: true });
};

// Clear synced changes
export const clearSyncedChanges = async () => {
  await offlineDb.pendingChanges.where('synced').equals(1).delete();
};

// Update cached unit
export const updateCachedUnit = async (unitId: string, updates: Partial<D2DUnit>) => {
  const existing = await offlineDb.units.get(unitId);
  if (existing) {
    await offlineDb.units.update(unitId, {
      ...updates,
      cached_at: new Date().toISOString()
    });
  }
};

// Add cached note
export const addCachedNote = async (note: D2DNote) => {
  await offlineDb.notes.put({
    ...note,
    cached_at: new Date().toISOString()
  });
};

// Delete cached note
export const deleteCachedNote = async (noteId: string) => {
  await offlineDb.notes.delete(noteId);
};
